/**
 * legBetCardStyles.js — StyleSheet for LegBetCard.
 * Extracted verbatim from components/LegBetCard.jsx (god-file decomposition).
 */

import { StyleSheet } from 'react-native';

export const styles = StyleSheet.create({
    // ═══════════════════════════════════════════════════════════════════════════
    // 🎨 MODERN CLEAN CARD DESIGN
    // ═══════════════════════════════════════════════════════════════════════════
    betCard: {
        position: 'relative',
        borderWidth: 1,
        // Subtle gradient border effect - lighter at top
        borderTopColor: 'rgba(255,255,255,0.15)',
        borderLeftColor: 'rgba(255,255,255,0.08)',
        borderRightColor: 'rgba(255,255,255,0.06)',
        borderBottomColor: 'rgba(255,255,255,0.04)',
        alignSelf: 'stretch',
        // Deep background with subtle transparency
        backgroundColor: 'rgba(12, 12, 18, 0.95)',
        opacity: 1,
        borderRadius: 16,
        justifyContent: 'flex-start',
        alignItems: 'center',
        overflow: 'visible', // ⭐ FIX: Allow content to be fully visible (buttons were getting cut off)
        // Subtle shadow for depth
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 8,
        // ⭐ FIX: Removed maxHeight to prevent button cutoff - let card grow naturally
        paddingBottom: 4, // Extra padding to ensure buttons aren't clipped
    },
    betCardBlur: {
        flex: 1,
        width: '100%',
        borderRadius: 16,
        padding: 16,
        paddingBottom: 20, // ⭐ FIX: Extra bottom padding for buttons
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible', // ⭐ FIX: Allow content to show fully
    },
    selectedBetCard: {
        // Vibrant accent when selected
        borderTopColor: 'rgba(99, 102, 241, 0.6)',
        borderLeftColor: 'rgba(99, 102, 241, 0.4)',
        borderRightColor: 'rgba(99, 102, 241, 0.3)',
        borderBottomColor: 'rgba(99, 102, 241, 0.2)',
        backgroundColor: 'rgba(12, 12, 22, 0.98)',
        shadowColor: '#6366F1',
        shadowOpacity: 0.25,
        shadowRadius: 16,
    },
    betCardContent: {
        flex: 1,
        alignSelf: 'stretch',
        gap: 14,
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingTop: 18,
        paddingBottom: 18,
        paddingLeft: 24,
        paddingRight: 18,
        opacity: 1,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TWO-COLUMN LAYOUT
    // ═══════════════════════════════════════════════════════════════════════════
    twoColumnLayout: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        alignItems: 'flex-start', // Don't stretch columns to match heights
        gap: 20,
    },
    leftColumn: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 14,
        minWidth: 200,
    },
    rightColumnFullHeight: {
        flexShrink: 0,
        width: 230,
        maxWidth: 250,
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.08)',
        paddingLeft: 14,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 450, // Match increased card height
        paddingBottom: 8, // Ensure last item isn't cut off
    },
    rightColumn: {
        flexShrink: 0,
        maxWidth: 260,
        minWidth: 180,
        alignItems: 'flex-start',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.06)',
        paddingLeft: 16,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // SECTIONS
    // ═══════════════════════════════════════════════════════════════════════════
    infoSection: {
        flexDirection: 'column',
        gap: 8,
    },
    mathSectionInline: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 12,
        paddingTop: 14,
        marginTop: 4,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.06)',
    },
    betCardActionsInline: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 10,
        flexDirection: 'row',
        flexWrap: 'wrap',
        paddingTop: 12,
        paddingBottom: 8, // ⭐ FIX: Ensure buttons have clearance at bottom
        marginTop: 4,
        marginBottom: 8, // ⭐ FIX: Extra margin at bottom
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.06)',
    },
    betCardHeader: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
    },
    betCardInfo: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        flexDirection: 'row',
    },
    betCardTitle: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 4,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // TYPOGRAPHY - Clean, Modern
    // ═══════════════════════════════════════════════════════════════════════════
    betCardTitleTextLarge: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.95)',
        fontSize: 17,
        fontWeight: '600',
        letterSpacing: -0.3,
        lineHeight: 22,
        opacity: 1,
    },
    betCardDescriptionLarge: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: 14,
        fontWeight: '500',
        lineHeight: 20,
        letterSpacing: 0.1,
        opacity: 1,
    },
    betCardTitleText: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 15,
        fontWeight: '500',
        lineHeight: 20,
        opacity: 1,
    },
    betCardDescription: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.65)',
        fontSize: 14,
        fontWeight: '400',
        lineHeight: 18,
        opacity: 1,
    },
    betCardAmount: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 14,
        fontWeight: '500',
        lineHeight: 18,
        opacity: 1,
    },
    betCardActions: {
        alignSelf: 'stretch',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        gap: 10,
        flexDirection: 'row',
    },
    betCardButton: {
        flex: 1,
        marginRight: 8,
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // SPORTSBOOKS SECTION
    // ═══════════════════════════════════════════════════════════════════════════
    booksSection: {
        alignSelf: 'stretch',
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.04)',
        paddingTop: 8
    },
    booksLabel: { color: 'rgba(255,255,255,0.5)', fontSize: 11, opacity: 1, letterSpacing: 0.5, textTransform: 'uppercase' },
    booksValue: { color: '#ffffff', fontSize: 13, fontWeight: '600', opacity: 1 },
    // ═══════════════════════════════════════════════════════════════════════════
    // MATH SECTION
    // ═══════════════════════════════════════════════════════════════════════════
    mathSection: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 10,
        marginTop: 6,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.08)'
    },
    mathHeaderRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 6,
    },
    mathHeader: {
        color: '#ffffff',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '700',
        opacity: 0.95,
    },
    mathGrid: {
        alignSelf: 'stretch',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
    },
    stakeControls: {
        flexDirection: 'column',
        gap: 6,
        alignItems: 'flex-end'
    },
    stakeQuickRow: {
        flexDirection: 'row',
        gap: 8
    },
    stakeChip: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.04)',
    },
    stakeChipActive: {
        backgroundColor: 'rgba(99, 102, 241, 0.25)',
        borderColor: 'rgba(99, 102, 241, 0.5)',
    },
    stakeChipText: {
        color: 'rgba(255,255,255,0.7)',
        fontWeight: '600',
        fontSize: 13,
        opacity: 1
    },
    stakeChipTextActive: {
        color: '#A5B4FC',
        fontWeight: '700',
        opacity: 1
    },
    stepperRow: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center'
    },
    stepperBtn: {
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)'
    },
    stepperText: {
        color: '#ffffff',
        fontWeight: '700',
        opacity: 1
    },
    stakeValue: {
        color: '#ffffff',
        fontWeight: '700',
        opacity: 1
    },
    stakeInputInline: {
        marginTop: 4,
        color: '#ffffff',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.12)',
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        minWidth: 90,
        backgroundColor: 'rgba(255,255,255,0.04)',
        fontSize: 14,
        opacity: 1
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // METRICS - Clean Grid Layout
    // ═══════════════════════════════════════════════════════════════════════════
    metricsRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        marginTop: 8
    },
    metricCol: {
        flex: 1,
        minWidth: 100,
        gap: 3
    },
    metricColFirst: {
        paddingLeft: 0
    },
    metricColSep: {
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.04)',
        paddingLeft: 14
    },
    metricLabel: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 11,
        fontWeight: '500',
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        opacity: 1
    },
    metricValue: {
        color: '#ffffff',
        fontSize: 18,
        lineHeight: 24,
        fontWeight: '700',
        letterSpacing: -0.3,
        opacity: 1
    },
    metricSubtext: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 11,
        fontWeight: '500',
        opacity: 1
    },
    bookDetailsBtn: {
        marginTop: 4,
        paddingVertical: 2,
        paddingHorizontal: 6,
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 4,
        alignSelf: 'flex-start'
    },
    bookDetailsBtnText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 10,
        opacity: 1
    },
    bookDetailsSection: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.1)',
        alignSelf: 'stretch'
    },
    bookDetailsTitle: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        fontWeight: '600',
        marginBottom: 8,
        opacity: 1
    },
    bookDetailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 4,
        opacity: 1
    },
    bookDetailName: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 11,
        opacity: 1
    },
    bookDetailOdds: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 11,
        fontWeight: '500',
        opacity: 1
    },
    // Modern EV colors - emerald green and rose red
    evPositive: { color: '#34D399', opacity: 1 },
    evNegative: { color: '#FB7185', opacity: 1 },
    mathRow: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 48,
    },
    mathItem: {
        flex: 1,
        height: 40,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
    },
    mathItemContent: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 8,
    },
    mathLabelValue: {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        flexDirection: 'row',
    },
    mathLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '600',
        lineHeight: 18,
        opacity: 1,
    },
    mathValue: {
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 18,
        opacity: 1,
    },
    mathValueWhite: {
        color: '#ffffff',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '600',
        lineHeight: 18,
        opacity: 1,
    },
    mathInput: {
        color: '#ffffff',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: 6,
        opacity: 1,
        paddingVertical: 6,
        paddingHorizontal: 8,
        minWidth: 80,
    },
    showAllBooksBtn: {
        marginTop: 8,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(59, 130, 246, 0.3)',
        alignItems: 'center',
    },
    showAllBooksBtnText: {
        color: '#60A5FA',
        fontSize: 11,
        fontWeight: '600',
    },
});
